// src/pages/clients/Statistics.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Fade,
  Zoom,
  Grow
} from '@mui/material';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  BarChart as BarChartIcon,
  DonutLarge as DonutIcon
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Importar el componente mejorado de distribución por plan
import PlanDistributionPanel from '../../components/statistics/PlanDistributionPanel';

// Definición de colores personalizados
const colors = {
  primary: '#4f46e5',    // Indigo
  secondary: '#06b6d4',  // Cyan
  tertiary: '#8b5cf6',   // Purple
  success: '#10b981',    // Emerald
  warning: '#f59e0b',    // Amber
  danger: '#ef4444',     // Red
  info: '#3b82f6',       // Blue
  grey: '#64748b',       // Slate
  charts: {
    total: '#4f46e5',     // Indigo
    active: '#10b981',    // Emerald
    inactive: '#f59e0b',  // Amber
    new: '#3b82f6',       // Blue
  },
  pieColors: ['#4f46e5', '#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6']
};

// Componente de tarjeta de estadísticas mejorado
const StatCard = ({ title, value, icon: Icon, color, subtitle, trend, delay = 0 }) => (
  <Zoom in={true} style={{ transitionDelay: `${delay * 100}ms` }}>
    <Card
      className="hover:shadow-lg transition-all duration-300"
      sx={{
        position: 'relative',
        overflow: 'visible',
        border: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        borderLeft: `4px solid ${color}`
      }}
    >
      <CardContent className="p-4">
        <Box className="flex items-center justify-between">
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.grey, fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="h4" className="mt-2 font-bold">
              {value}
            </Typography>
            {subtitle && (
              <Box className="flex items-center mt-1">
                {trend && trend !== 0 && (
                  <>
                    {trend > 0 ? (
                      <TrendingUpIcon sx={{ color: colors.success, marginRight: '4px' }} fontSize="small" />
                    ) : (
                      <TrendingDownIcon sx={{ color: colors.danger, marginRight: '4px' }} fontSize="small" />
                    )}
                    <Typography
                      variant="caption"
                      sx={{ color: trend > 0 ? colors.success : colors.danger, fontWeight: 'bold' }}
                    >
                      {Math.abs(trend)}%
                    </Typography>
                  </>
                )}
                <Typography variant="caption" sx={{ color: colors.grey, marginLeft: 1 }}>
                  {subtitle}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            className="w-14 h-14 rounded-full flex items-center justify-center"
            sx={{
              backgroundColor: `${color}20`,
              boxShadow: `0 0 0 8px ${color}05`,
            }}
          >
            <Icon sx={{ color: color, fontSize: 28 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  </Zoom>
);

// Tooltip personalizado para gráficos
const CustomTooltip = ({ active, payload, label, valuePrefix = '', valueSuffix = '' }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          border: '1px solid #f1f5f9',
          borderRadius: '8px',
          padding: '10px 14px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography sx={{ color: colors.grey, fontWeight: 600, marginBottom: '4px' }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={`item-${index}`} className="flex items-center mt-1">
            <Box
              sx={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: entry.color,
                marginRight: '8px',
              }}
            />
            <Typography sx={{ color: colors.grey, fontSize: '0.875rem' }}>
              {entry.name}: {valuePrefix}{entry.value.toLocaleString()}{valueSuffix}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

const Statistics = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    activeClients: 0,
    newClients: 0,
    churnRate: 0,
    trends: {
      clientGrowth: 0,
      activeGrowth: 0,
      newGrowth: 0
    }
  });
  const [growthData, setGrowthData] = useState([]);
  const [planDistribution, setPlanDistribution] = useState([]);
  const [zoneDistribution, setZoneDistribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchStatistics();
  }, [selectedYear]);

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/client-statistics?year=${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Verificar que los datos estén completos
      if (!data.stats) {
        throw new Error('Respuesta incompleta del servidor');
      }

      setStats(data.stats);
      setGrowthData(data.growthData || []);
      setPlanDistribution(data.planDistribution || []);
      setZoneDistribution(data.zoneDistribution || []);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError(`Error al cargar estadísticas: ${error.message}`);

      // Establecer datos por defecto para evitar errores de renderizado
      setStats({
        totalClients: 0,
        activeClients: 0,
        newClients: 0,
        churnRate: 0,
        trends: {
          clientGrowth: 0,
          activeGrowth: 0,
          newGrowth: 0
        }
      });
      setGrowthData([]);
      setPlanDistribution([]);
      setZoneDistribution([]);
    } finally {
      setLoading(false);
    }
  };

  // Datos de ejemplo para pruebas de desarrollo
  const samplePlanDistribution = useMemo(() => [
    { name: "Plan Básico 10M/10M", value: 35, percentage: 31.8 },
    { name: "Plan Intermedio 20M/20M", value: 42, percentage: 38.2 },
    { name: "Plan Full 30M/30M", value: 33, percentage: 30.0 }
  ], []);

  if (loading) {
    return (
      <Box className="p-6 flex flex-col items-center justify-center min-h-[400px]">
        <CircularProgress size={60} sx={{ color: colors.primary }} />
        <Typography sx={{ color: colors.grey, marginTop: 2 }}>
          Cargando estadísticas de clientes...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Fade in={true} timeout={500}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            Estadísticas de Clientes
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120, alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.1)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary,
                },
              }}
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      </Fade>

      {error && (
        <Fade in={true}>
          <Alert
            severity="error"
            className="mb-6"
            sx={{
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
          >
            {error}
          </Alert>
        </Fade>
      )}

      <Grid container spacing={3} className="mb-6">
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Clientes"
            value={stats.totalClients}
            icon={PeopleIcon}
            color={colors.primary}
            subtitle="Total de clientes"
            trend={stats.trends?.clientGrowth}
            delay={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Clientes Activos"
            value={stats.activeClients}
            icon={PeopleIcon}
            color={colors.success}
            subtitle={`${((stats.activeClients / stats.totalClients) * 100 || 0).toFixed(1)}% del total`}
            trend={stats.trends?.activeGrowth}
            delay={1}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Nuevos Clientes"
            value={stats.newClients}
            icon={TrendingUpIcon}
            color={colors.info}
            subtitle="Este mes"
            trend={stats.trends?.newGrowth}
            delay={2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tasa de Deserción"
            value={`${stats.churnRate}%`}
            icon={SpeedIcon}
            color={colors.danger}
            subtitle="Últimos 30 días"
            delay={3}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Grow in={true} timeout={800}>
            <Paper
              className="p-4 hover:shadow-lg transition-all duration-300"
              sx={{
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.05)',
                minWidth: 0
              }}
            >
              <Box className="flex justify-between items-center mb-4">
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Crecimiento de Clientes
                </Typography>
                <BarChartIcon sx={{ color: colors.grey }} />
              </Box>
              <Box className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={growthData}>
                    <defs>
                      <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={colors.charts.total} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={colors.charts.total} stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorActive" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={colors.charts.active} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={colors.charts.active} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="month"
                      tick={{ fill: colors.grey }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis
                      tick={{ fill: colors.grey }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{
                        paddingTop: '10px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke={colors.charts.total}
                      strokeWidth={3}
                      fill="url(#colorTotal)"
                      name="Total Clientes"
                      activeDot={{ r: 8, strokeWidth: 1, stroke: '#fff' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="active"
                      stroke={colors.charts.active}
                      strokeWidth={3}
                      fill="url(#colorActive)"
                      name="Clientes Activos"
                      activeDot={{ r: 8, strokeWidth: 1, stroke: '#fff' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grow>
        </Grid>

        <Grid item xs={12} md={4}>
          <Grow in={true} timeout={900}>
            {/* Aquí usamos el componente mejorado de distribución por plan */}
            <Box>
              <PlanDistributionPanel
                planDistribution={planDistribution}
              />
            </Box>
          </Grow>
        </Grid>

        <Grid item xs={12}>
          <Grow in={true} timeout={1000}>
            <Paper
              className="p-4 hover:shadow-lg transition-all duration-300"
              sx={{
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.05)',
                minWidth: 0
              }}
            >
              <Box className="flex justify-between items-center mb-4">
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Clientes por Zona
                </Typography>
                <BarChartIcon sx={{ color: colors.grey }} />
              </Box>
              <Box className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={zoneDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: colors.grey }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis
                      tick={{ fill: colors.grey }}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <Tooltip
                      content={<CustomTooltip valueSuffix=" clientes" />}
                    />
                    <Legend
                      wrapperStyle={{
                        paddingTop: '10px',
                      }}
                    />
                    <Bar
                      dataKey="active"
                      fill={colors.charts.active}
                      name="Clientes Activos"
                      radius={[4, 4, 0, 0]}
                      barSize={40}
                    />
                    <Bar
                      dataKey="inactive"
                      fill={colors.charts.inactive}
                      name="Clientes Inactivos"
                      radius={[4, 4, 0, 0]}
                      barSize={40}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grow>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Statistics;